import { useEffect, useRef, useState } from 'react';
import { ProjectType } from '@bulthaup/api/db';
import { Group, NumberInput, Select } from '@mantine/core';
import { useDebounceFn } from 'ahooks';

import { trpc } from '../../../../../../../services/trpc';

export function EditQuoteMetaSection({ projectId }: { projectId: string }) {
  const { data: project } = trpc.projects.get.useQuery({ id: projectId });

  const [paymentTypeOnSignature, setPaymentTypeOnSignature] =
    useState<string>('COMMANDE');
  const [
    paymentPercentRequiredOnSignature,
    setPaymentPercentRequiredOnSignature,
  ] = useState<number | null>(null);

  const updateMutation = trpc.projects.updateInfoOnSignature.useMutation();
  const hasHydratedRef = useRef(false);

  // Hydrate state from project data on first load
  useEffect(() => {
    if (project && !hasHydratedRef.current) {
      setPaymentTypeOnSignature(project.paymentTypeOnSignature);
      const existingValue =
        project.paymentPercentRequiredOnSignature?.toNumber();
      if (existingValue != null) {
        setPaymentPercentRequiredOnSignature(existingValue);
      } else {
        const defaultValue =
          project.type === ProjectType.AFTER_SALES ? 100 : 35;
        setPaymentPercentRequiredOnSignature(defaultValue);
        // Save default value
        updateMutation.mutate({
          id: projectId,
          paymentTypeOnSignature: project.paymentTypeOnSignature,
          paymentPercentRequiredOnSignature: defaultValue,
        });
      }
      hasHydratedRef.current = true;
    }
  }, [project, projectId, updateMutation]);

  const { run: debouncedUpdate } = useDebounceFn(
    (
      paymentTypeOnSignature: string,
      paymentPercentRequiredOnSignature: number,
    ) => {
      updateMutation.mutate({
        id: projectId,
        paymentTypeOnSignature,
        paymentPercentRequiredOnSignature,
      });
    },
    { wait: 500 },
  );

  if (!project) {
    return null;
  }

  return (
    <Group>
      <Select
        label="Type de paiement demandé"
        data={[
          { value: 'COMMANDE', label: 'Acompte pour commande' },
          { value: 'PROVISION_SUR_ETUDE', label: 'Provision sur étude' },
        ]}
        value={paymentTypeOnSignature}
        onChange={val => {
          const newPaymentTypeOnSignature = val || 'COMMANDE';
          setPaymentTypeOnSignature(newPaymentTypeOnSignature);
          debouncedUpdate(
            newPaymentTypeOnSignature,
            paymentPercentRequiredOnSignature ?? 0,
          );
        }}
      />
      <NumberInput
        label="Pourcentage à régler à la signature"
        min={0}
        max={100}
        step={0.01}
        rightSection="%"
        value={paymentPercentRequiredOnSignature ?? undefined}
        onChange={val => {
          const num = typeof val === 'number' ? val : Number(val);
          setPaymentPercentRequiredOnSignature(num);
          debouncedUpdate(paymentTypeOnSignature, num);
        }}
      />
    </Group>
  );
}
